export const en = {
  common: {
    app: {
      ownerName: "Riccardo",
      logoAlt: "BlackFlag logo",
      homeLinkAriaLabel: "Go to homepage",
    },
    navigation: {
      ariaLabel: "Primary navigation",
      home: "Home",
      projects: "Projects",
      skills: "Skills",
      about: "About",
      playground: "Playground",
      contact: "Contact",
      openMenu: "Open navigation menu",
      switchThemeToLight: "Switch to light theme",
      switchThemeToDark: "Switch to dark theme",
      switchLanguage: "Change language",
    },
    languages: {
      it: "IT",
      en: "EN",
      fr: "FR",
    },
    aboutPage: {
      eyebrow: "Profile",
      title:
        "A solid foundation between discipline, software engineering, and scalable systems",
      imageAlt: "Portrait of Riccardo Licciardi",
      presentation: {
        eyebrow: "Presentation video",
        title: "A quick introduction to my profile in 2 minutes",
        frameTitle: "Riccardo Licciardi presentation video",
      },
      paragraphs: {
        first:
          "Riccardo Licciardi is a Frontend Engineer with 5 years of experience building scalable web applications for high-traffic platforms. He works with React and Angular on complex products, where structural refactoring, code quality, and maintainability are practical levers to improve performance and long-term sustainability.",
        second:
          "Over the last few years, he has strengthened an approach centered on modular architecture, clear workflows, and the gradual evolution of complex codebases. Today he is expanding toward a fullstack profile, integrating Node.js, Python, FastAPI, and expertise in AI, LLMs, STT/TTS, and real-time communication.",
        third:
          "Before software, he served in the Italian Army with the 4th Alpine Paratroopers Regiment, within the Special Operations Forces. The discipline, responsibility, and determination shaped in that experience are now part of how he approaches projects, teams, and technical decisions.",
      },
    },
    homeSkillsCard: {
      tabs: {
        frontend: "Frontend",
        backend: "Backend",
        goals: "Future goals",
      },
      sections: {
        frontend: {
          eyebrow: "Frontend Stack",
          title: "Crafting polished interfaces",
          description:
            "Designs scalable front-ends by organizing complex codebases through modular architectures and refactoring to enhance performance, maintainability, and product evolution speed",
        },
        backend: {
          eyebrow: "Backend Stack",
          title: "Designing reliable application logic",
          description:
            "Expanding into backend development and artificial intelligence, he is consolidating the skills required to orchestrate complex business processes, real-time voice pipelines, and structured data flows for automation.",
        },
        goals: {
          eyebrow: "Future Goals",
          title: "Growing depth and range",
          description:
            "I am investing in stronger system design, cloud fluency, and broader full-stack confidence over time.",
        },
      },
      skills: {
        react: "React",
        typescript: "TypeScript",
        materialUi: "Material UI",
        htmlCss: "HTML / CSS",
        nodejs: "Node.js",
        express: "Express",
        restApi: "REST API Design",
        sqlDataModeling: "SQL / Data Modeling",
        java: "Java",
        csharp: "C# / .NET",
        cloudDevops: "Cloud & DevOps",
        systemDesign: "System Design",
      },
      buttons: {
        viewProjects: "View projects",
        contactMe: "Contact me",
        details: "Details",
      },
    },
    skillsPage: {
      eyebrow: "Skills",
      title: "Active stack, key tools, and growth trajectory",
      description:
        "A broader overview of the technologies I use to build interfaces, backend integrations, and evolving workflows. Each block stays compact: practical focus, usage context, and related stack.",
      searchPlaceholder: "Search React, FastAPI, Docker, PostgreSQL...",
      searchButton: "Search",
      searchAriaLabel: "Search technologies",
      emptyStateTitle: "No technologies found",
      emptyStateDescription:
        "Try a different tag or clear the search to see the full skill set.",
      sections: {
        frontend: {
          eyebrow: "Frontend",
          title: "Modular UI and high-maintenance products",
          description:
            "Technologies and libraries used to build scalable interfaces, refine components, and manage complex codebases.",
        },
        backend: {
          eyebrow: "Backend & AI",
          title: "Services, integrations, and real-time pipelines",
          description:
            "Stack used for APIs, process orchestration, and conversational or data-driven workflows.",
        },
        goals: {
          eyebrow: "Future goals",
          title: "Fullstack growth and architectural depth",
          description:
            "Areas I am strengthening to increase end-to-end autonomy, system quality, and project solidity.",
        },
      },
      tags: {
        react: "React",
        reduxToolkit: "Redux Toolkit",
        vite: "Vite",
        typescript: "TypeScript",
        angular: "Angular",
        kendoUi: "Kendo UI",
        restApi: "REST APIs",
        javascript: "JavaScript",
        materialUi: "Material UI",
        tailwindCss: "Tailwind CSS",
        htmlCss: "HTML / CSS",
        figma: "Figma",
        nodejs: "Node.js",
        python: "Python",
        fastapi: "FastAPI",
        openaiApi: "OpenAI API",
        geminiApi: "Gemini API",
        websockets: "WebSockets",
        asterisk: "Asterisk",
        sttTts: "STT / TTS",
        postgresql: "PostgreSQL",
        docker: "Docker",
        cicd: "CI/CD",
        git: "Git",
        cloud: "Cloud",
        systemDesign: "System Design",
        csharp: "C# / .NET",
        sqlServer: "SQL Server",
        architecture: "Modular Architecture",
        observability: "Observability",
        llm: "LLM",
      },
      items: {
        frontend: {
          react: {
            title: "React Ecosystem",
            summary:
              "Modular interfaces, refactoring, and scalability for complex products.",
          },
          angular: {
            title: "Angular Enterprise",
            summary:
              "Enterprise platforms, operational dashboards, and complex workflows.",
          },
          typescript: {
            title: "TypeScript First",
            summary:
              "Strong typing to reduce regressions and improve maintainability.",
          },
          ui: {
            title: "UI Systems",
            summary:
              "Cohesive design systems, reusable components, and responsive development.",
          },
        },
        backend: {
          node: {
            title: "Node.js Services",
            summary:
              "Server logic and full-stack services focused on operational efficiency.",
          },
          python: {
            title: "Python & FastAPI",
            summary:
              "Lean APIs, process orchestration, and advanced AI integrations.",
          },
          realtime: {
            title: "Real-Time Voice Workflows",
            summary:
              "Conversational pipelines and event-driven automation for Voice AI.",
          },
          data: {
            title: "Data & Delivery",
            summary:
              "Persistence, containers, and CI/CD for stable, evolvable environments.",
          },
        },
        goals: {
          architecture: {
            title: "System Design",
            summary:
              "Trade-off analysis, modularity, and scalability of complex systems.",
          },
          cloud: {
            title: "Cloud & DevOps",
            summary:
              "Consolidating deployment and pipelines for continuous delivery.",
          },
          dotnet: {
            title: "C# / .NET",
            summary:
              "Expanding into enterprise backend and strongly typed integrations.",
          },
          ai: {
            title: "AI Reliability",
            summary:
              "Optimizing LLM and STT/TTS workflows with a focus on robustness.",
          },
        },
      },
    },
    homeIntro: {
      chip: "Frontend Engineer",
      title: "Riccardo Licciardi",
      description:
        "Frontend Engineer focused on scalability, maintainability, and architectural quality. He builds solid web products and is expanding toward a fullstack profile with an emphasis on AI and real-time systems.",
      rotatingSentences: {
        first: "Modular architectures for complex products",
        second: "Refactoring that improves performance",
        third: "AI workflows and real-time systems built for execution",
      },
    },
    homeContact: {
      eyebrow: "Contact",
      title: "Let's talk about product, architecture, or new collaborations",
      description:
        "If you are building a web platform, a complex interface, or a real-time workflow with AI integration, you can contact me directly here. I prefer clear, fast conversations focused on execution.",
      items: {
        email: {
          label: "Email",
          helper: "Tap to copy the email address",
        },
        phone: {
          label: "Phone",
          helper: "Tap to copy the phone number",
        },
      },
      copied: "Copied to clipboard",
      cta: "Go to contact page",
    },
    contactPage: {
      eyebrow: "Contact",
      title: "Direct contact and quick message",
      description:
        "If you want to discuss a project, a collaboration, or an idea to turn into a product, here you can find my direct contact details and a simple way to open a prefilled email immediately.",
      copied: "Copied to clipboard",
      open: "Open",
      items: {
        email: {
          label: "Email",
          helper: "Tap to copy the email address",
        },
        phone: {
          label: "Phone",
          helper: "Tap to copy the phone number",
        },
      },
      form: {
        eyebrow: "Message",
        title: "Send a direct message",
        description:
          "This form opens your email client with prefilled details so you can contact me quickly without a dedicated backend.",
        fields: {
          name: "Name",
          email: "Email",
          message: "Message",
        },
        submit: "Open email draft",
        reset: "Reset",
        subjectPrefix: "Portfolio contact from",
        fallbackContactName: "a new contact",
        bodyLabels: {
          name: "Name",
          email: "Email",
        },
      },
    },
    homeProjects: {
      eyebrow: "Selected projects",
      title: "Three quick previews of my work",
      description:
        "A compact selection of projects showing architectural refactoring, real-time voice AI systems, and full-stack data-driven tools.",
      previewLabel: "Preview",
      viewAll: "View all projects",
      items: [
        {
          title: "Digital Voting Platform (Opinio)",
          category: "High-scale frontend",
          description:
            "Complete frontend rebuild for an election platform serving more than 800,000 users.",
          tags: ["React", "Redux Toolkit", "Vite"],
        },
        {
          title: "AI Voice PBX",
          category: "Real-time Voice AI",
          description:
            "Multi-tenant intelligent PBX with STT, LLM, and TTS pipeline.",
          tags: ["Python", "FastAPI", "Asterisk"],
        },
        {
          title: "Restaurant Expense Analyzer",
          category: "Full-stack analytics",
          description:
            "Automated PDF invoice analysis with AI and interactive dashboards.",
          tags: ["React", "Node.js", "TypeScript"],
        },
        {
          title: "DINAMO Retail Platform",
          category: "Retail microservices",
          description:
            "Retail platform for checkout, warehouse, and large-scale operational workflows.",
          tags: ["Angular", "Kendo UI", "REST APIs"],
        },
        {
          title: "Professional Training Platform (CNAPPC)",
          category: "Multi-role dashboards",
          description:
            "CFP credit management and purchase flows for more than 100,000 professionals.",
          tags: ["React", "TypeScript", "REST APIs"],
        },
        {
          title: "Workshop Management & Realtime Maps",
          category: "Realtime maps and tracking",
          description:
            "QR-based work tracking with realtime geospatial visualization.",
          tags: ["Node.js", "PostgreSQL", "Leaflet"],
        },
      ],
    },
    testimonials: {
      eyebrow: "Testimonials",
      title: "Feedback from complex project environments",
      description:
        "A collection of feedback highlighting reliability, project vision, and the ability to turn complex needs into solid technical solutions.",
      items: [
        {
          author: "Michele Albertini",
          role: "CEO Didanet",
          quote:
            "Riccardo stood out for strong personal initiative and solid project vision. He consistently maintained high quality standards and contributed effectively to problem solving even in complex environments.",
        },
        {
          author: "V3L Studio",
          role: "Consulting and development studio",
          quote:
            "Working with Riccardo has become a strategic asset for our studio. He translates complex visions into solid architectures and handles challenging Voice AI and real-time pipeline projects with full ownership.",
        },
        {
          author: "Dr. Elena V.",
          role: "Notary",
          quote:
            "Riccardo translated the need for reliability and rigor into an institutional website that communicates authority and clarity, with excellent attention to detail and technical precision.",
        },
        {
          author: "Dr. Valerio Noto",
          role: "Notary",
          quote:
            "His technical precision and attention to detail exceeded expectations, successfully communicating the authority of my firm through both branding and website work.",
        },
        {
          author: "Bea",
          role: "Spiritual Guide & Mentor",
          quote:
            "Riccardo brought my vision to life by combining technical complexity with a calm, thoughtful user experience tailored to the soul of the project.",
        },
      ],
    },
    projects: {
      eyebrow: "Core projects and experience",
      title: "Scalable platforms, voice AI, and data-driven workflows",
      description:
        "A selection of projects spanning architectural refactoring, high-traffic products, full-stack platforms, and real-time systems integrated with AI.",
      previewLabel: "Project",
      openDetails: "Open details",
      close: "Close",
      stackLabel: "Stack",
      detailsLabel: "Details",
      items: [
        {
          title: "Digital Voting Platform (Opinio)",
          role: "Frontend Engineer",
          category: "High-scale frontend",
          description:
            "Riccardo led the transition from an obsolete legacy foundation to a modern scalable web platform, rebuilding the frontend architecture with a modular React approach to recover delays, improve maintainability, and support secure election processes for more than 800,000 users.",
          details: [
            "Critical transition from an outdated legacy architecture to a modern scalable web platform.",
            "Complete frontend rebuild with a modular approach and React best practices.",
            "Support for secure election processes serving more than 800,000 users.",
          ],
          stack: [
            "React",
            "Redux Toolkit",
            "Vite",
            "Material UI",
            "Blockchain",
          ],
        },
        {
          title: "AI Voice PBX",
          role: "Fullstack / AI Engineer",
          category: "Real-time Voice AI",
          description:
            "Riccardo designed a multi-tenant voice AI platform able to manage real-time phone conversations, extract structured data through custom tools, and trigger automated business workflows such as lead generation and delivery.",
          details: [
            "Scalable voice AI platform for automated phone reception and data collection.",
            "Real-time STT → LLM → TTS pipeline with structured data extraction through custom tools.",
            "Multi-tenant architecture built for business workflows across different verticals.",
          ],
          stack: [
            "Python",
            "FastAPI",
            "Asterisk",
            "Gemini STS",
            "OpenAI API",
            "Docker",
            "PostgreSQL",
          ],
        },
        {
          title: "Restaurant Expense Analyzer",
          role: "Fullstack Developer",
          category: "Full-stack analytics",
          description:
            "Riccardo built a full-stack tool for automated analysis of unstructured PDF invoices, combining AI-powered extraction, interactive dashboards, and export workflows to support accounting decisions on dense datasets.",
          details: [
            "Bulk upload of PDF invoices with automated granular data extraction through AI.",
            "Interactive dashboards to aggregate costs and visualize spending distribution.",
            "Export workflows designed to support accounting and operational decisions.",
          ],
          stack: [
            "React",
            "Node.js",
            "TypeScript",
            "Tailwind CSS",
            "Recharts",
            "LLM Integration",
          ],
        },
        {
          title: "DINAMO Retail Platform",
          role: "Frontend Engineer",
          category: "Retail microservices",
          description:
            "Riccardo contributed to a microservices-based retail platform for checkout and warehouse operations, implementing operational features, integrating complex endpoints, and improving the efficiency of legacy components.",
          details: [
            "Development of features for checkout operations and warehouse management in a microservices architecture.",
            "Integration of complex endpoints for sales and logistics systems.",
            "Improved efficiency of legacy components in high-activity retail workflows.",
          ],
          stack: ["Angular", "Kendo UI", "REST APIs"],
        },
        {
          title: "Professional Training Platform (CNAPPC)",
          role: "Frontend Engineer",
          category: "Multi-role dashboards",
          description:
            "Riccardo designed and delivered key features for CFP credit management and multi-role dashboards, building purchasing and monitoring flows for a platform serving more than 100,000 professionals.",
          details: [
            "Designed features from scratch for CFP credit monitoring.",
            "Built multi-role dashboards for Orders, Providers, and Professionals.",
            "Implemented purchase and monitoring flows for more than 100,000 members.",
          ],
          stack: ["React", "TypeScript", "REST APIs"],
        },
        {
          title: "Workshop Management & Realtime Maps",
          role: "Fullstack Developer",
          category: "Realtime maps and tracking",
          description:
            "Riccardo developed a QR-based work tracking platform and an interactive geospatial map for the district heating network in Asiago, combining backend logic, territorial data, and realtime visualization.",
          details: [
            "Platform for tracking activities through QR codes.",
            "Interactive geospatial map for managing the district heating network in Asiago.",
            "Integration of backend logic, territorial data, and realtime visualization.",
          ],
          stack: ["Node.js", "PostgreSQL", "Leaflet", "JavaScript"],
        },
      ],
    },
  },
} as const;
